import { getFromBreakpoint } from '@vakantiesnl/components/src/styles/breakpoints';
import { makeStyles } from '@vakantiesnl/components/src/styles/themes/themewrapper';

export const useStyles = makeStyles<
	void,
	| 'figure'
	| 'imageContainer'
	| 'image'
	| 'heading'
	| 'mainInfo'
	| 'offerWrapper'
	| 'ratingBox'
	| 'content'
	| 'details'
	| 'price'
	| 'disclaimer'
	| 'contentContainer'
	| 'pricewrapper'
	| 'priceLarge'
	| 'button'
	| 'favoriteButton'
>({ name: 'ProductCard', uniqId: 'product-card' })((theme, _, classes) => ({
	productCardContainer: {
		position: 'relative',
		display: 'flex',
		background: theme.palette.neutral[0],

		'&:hover': {
			[`& .${classes.button}`]: {
				backgroundColor: theme.button.primary.background.color.hover,
			},
		},
	},
	tiny: {
		[`& .${classes.imageContainer}`]: {
			height: '216px',
		},
	},
	large: {
		[`& .${classes.figure}`]: {
			flexDirection: 'row',
			columnGap: theme.spacing.small,
		},
		[`& .${classes.contentContainer}`]: {
			flexDirection: 'row',
			columnGap: theme.spacing.small,
			height: '240px',
			display: 'flex',
			flex: 1,
			minWidth: 'calc(100% - 32px - 320px - 160px)',
		},
		[`& .${classes.imageContainer}`]: {
			width: '320px',
			height: '100%',
		},
		[`& .${classes.heading}`]: {
			height: '40px',
			display: 'flex',
			alignItems: 'center',
		},
		[`& .${classes.mainInfo}`]: {
			position: 'initial',
			flex: 1,
		},
		[`& .${classes.offerWrapper}`]: {
			alignItems: 'flex-end',
			gap: theme.spacing.xsmall,
		},
		[`& .${classes.ratingBox}`]: {
			top: 0,
		},
		[`& .${classes.disclaimer}`]: {
			textAlign: 'right',
			marginTop: theme.spacing.xxsmall,
		},
		[`& .${classes.content}`]: {
			padding: 0,
			display: 'flex',
			flex: 1,
			minWidth: 'calc(100% - 32px - 320px - 160px)',
		},
		[`& .${classes.details}`]: {
			padding: 0,
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'flex-end',
			height: '100%',
			alignItems: 'flex-end',
			width: '160px',
		},
	},
	favoriteButton: {
		position: 'absolute',
		right: theme.spacing.xsmall,
		top: theme.spacing.xsmall,
		zIndex: theme.zIndex.lifted,
		color: theme.button.secondary.label.color.default,

		'&&': {
			backgroundColor: theme.palette.neutral[0],
		},

		'&:hover': {
			borderColor: theme.button.secondary.border.color.default,
			color: theme.body.foreground.color.mandatory,
		},
	},
	favoriteButtonActive: {
		color: theme.body.foreground.color.mandatory,

		'&:hover': {
			color: 'initial',
		},
	},
	map: {
		[`& .${classes.imageContainer}`]: {
			display: 'none',

			[getFromBreakpoint('md')]: {
				display: 'initial',
				borderRadius: 0,
			},
		},
		[`& .${classes.contentContainer}`]: {
			padding: theme.spacing.small,
			[getFromBreakpoint('md')]: {
				padding: `0 ${theme.spacing.small} ${theme.spacing.small}`,
			},
		},
		[`& .${classes.priceLarge}`]: {
			textAlign: 'left',

			[getFromBreakpoint('md')]: {
				textAlign: 'right',
			},
		},
		[`& .${classes.pricewrapper}`]: {
			flexDirection: 'row',
			gap: theme.spacing.medium,
		},
	},
	contentContainer: {
		display: 'flex',
		flexDirection: 'column',
		rowGap: theme.spacing.small,
	},
	figure: {
		display: 'flex',
		flexDirection: 'column',
		rowGap: theme.spacing.small,
		width: '100%',
	},
	imageContainer: {
		display: 'flex',
		position: 'relative',
		width: '100%',
		height: '240px',
		flex: '0 0 auto',
		overflow: 'hidden',
		borderRadius: theme.borderRadius.rounded,
	},
	image: {
		objectFit: 'cover',
	},
	heading: {},
	headingTitleFullWidth: {
		width: '100%',
	},
	headingTitle: {
		/** Prevents touching the ratingbox on mobile */
		width: `calc(100% - ${theme.spacing.xxlarge})`,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	mainInfo: {
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing.xxsmall,
		minWidth: 'inherit',
		position: 'relative',
	},
	infoItem: {
		display: 'flex',
		flexDirection: 'row',
	},
	ratingBox: {
		position: 'absolute',
		right: 0,
		top: 0,
	},
	details: {},
	content: {},
	gradient: {
		position: 'absolute',
		top: 0,
		left: 0,
		width: '100%',
		height: '100%',
		background: `linear-gradient(0deg, ${theme.card.gradient.start} 0%, ${theme.card.gradient.end} 50%);`,
	},
	priceLarge: {
		width: 'auto',
		textAlign: 'right',

		/** Override price styles to be more specific */
		'&& span,p': {
			alignSelf: 'flex-end',
			color: theme.palette.neutral[80],
		},
	},
	price: {
		position: 'absolute',
		width: 'auto',
		right: theme.spacing.small,
		bottom: theme.spacing.small,
		textAlign: 'right',

		/** Override price styles to be more specific */
		'&& span,p': {
			color: theme.palette.neutral[0],
			alignSelf: 'flex-end',
		},
	},
	zooverDealPrice: {
		/** Add zoover deal bar height to the spacing */
		bottom: `calc(${theme.spacing.small} + ${theme.spacing.xlarge})`,
	},
	pricewrapper: {
		display: 'flex',
		alignItems: 'flex-end',
		flexDirection: 'column',
	},
	offerWrapper: {
		display: 'flex',
		alignItems: 'flex-start',
		flexDirection: 'column',
		gap: theme.spacing.xsmall,
	},
	offerIncludesMessage: {
		color: theme.body.foreground.color.default,
	},
	button: {
		width: '100%',
	},
	link: {
		position: 'absolute',
		top: 0,
		bottom: 0,
		left: 0,
		right: 0,
		width: '100%',
		height: '100%',
		/** Hide link text */
		span: {
			display: 'block',
			height: 0,
			overflow: 'hidden',
		},
	},
	disclaimer: {
		marginTop: 0,
		textAlign: 'left',
	},
	breadCrumbs: {
		gap: theme.spacing.xxsmall,

		'& *': {
			color: theme.body.foreground.color.default,
		},

		span: {
			font: theme.typography.labelSm,
		},
	},
	zooverDeal: {
		position: 'absolute',
		bottom: 0,
		boxSizing: 'border-box',
		width: '100%',
	},
	accoType: {
		color: theme.body.foreground.color.default,
	},
}));
